import React from "react"

/**
 * The folded-map-and-pin artwork the approved mockup uses for both of this
 * page's large empty states — "Map unavailable" inside the map rectangle, and
 * "Select a marker to view details" in the details column. One component, two
 * sizes, so the two states cannot drift into different illustration styles.
 *
 * Inline SVG rather than an asset file or a remote image: it has to inherit
 * the page's own palette (the pin is the EZBuild teal, the map planes are the
 * slate ramp), it has to stay crisp at both sizes, and the task forbids an
 * external URL. Drawn flat-but-dimensional — two stacked map planes in
 * different tints read as a fold, and the pin casts a soft ellipse — matching
 * the mockup's restrained depth without becoming a 3D illustration.
 *
 * Decorative only: every call site supplies its own heading and body copy, so
 * this carries `aria-hidden` and contributes nothing to the accessible name.
 */
export function MapIllustration({ width = 200 }: { width?: number }) {
  // The artwork is authored against a 200x146 box and scaled from there, so a
  // caller only ever picks a width and the proportions follow.
  const height = Math.round((width * 146) / 200)

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 200 146"
      fill="none"
      aria-hidden="true"
      className="shrink-0"
    >
      {/* Clouds — the mockup's own faint depth cues, behind everything. */}
      <g opacity="0.55">
        <path
          d="M24 46c0-5 4-9 9-9 2.2 0 4.2.8 5.8 2.1A7.6 7.6 0 0 1 53 42.7c3.6.3 6.4 3.3 6.4 7 0 .9-.2 1.8-.5 2.6H26.9A8.9 8.9 0 0 1 24 46Z"
          fill="#DCEAF7"
        />
        <path
          d="M146 34c0-4.3 3.5-7.8 7.8-7.8 1.9 0 3.6.7 5 1.8a6.6 6.6 0 0 1 11.4 3.1c3.1.3 5.5 2.9 5.5 6 0 .8-.1 1.5-.4 2.2h-28a7.7 7.7 0 0 1-1.3-5.3Z"
          fill="#DCEAF7"
        />
      </g>

      {/* Ground shadow under the whole composition. */}
      <ellipse cx="100" cy="128" rx="62" ry="8" fill="#0F172A" opacity="0.06" />

      {/* Lower map plane — the far half of the fold. */}
      <path
        d="M38 100 100 72l62 28-62 28-62-28Z"
        fill="#C7DCF0"
        opacity="0.55"
      />

      {/* Upper map plane, with its own fold crease and route lines. */}
      <path
        d="M38 92 100 64l62 28-62 28-62-28Z"
        fill="#E8F1FA"
        stroke="#BFD6EC"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M100 64v56" stroke="#BFD6EC" strokeWidth="1.5" />
      <path
        d="M62 92c10-5 18-3 26 1M112 97c10-4 18-3 26 1"
        stroke="#BFD6EC"
        strokeWidth="2"
        strokeLinecap="round"
      />

      {/* The pin — EZBuild teal, lifted off the map by its own soft shadow. */}
      <ellipse cx="100" cy="86" rx="13" ry="4" fill="#0F172A" opacity="0.1" />
      <path
        d="M100 20c-12.7 0-23 10.3-23 23 0 17.3 23 41 23 41s23-23.7 23-41c0-12.7-10.3-23-23-23Z"
        fill="#2A7D8C"
      />
      <path
        d="M100 20c-12.7 0-23 10.3-23 23 0 17.3 23 41 23 41V20Z"
        fill="#fff"
        opacity="0.12"
      />
      <circle cx="100" cy="42" r="8.5" fill="#fff" />
    </svg>
  )
}

export default MapIllustration
